import { JSX, useEffect, useState } from "react";
import { Switch, Table, TableColumnsType, Tag, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import Text from "../../components/Text/Text";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import { CardContainer } from "../../components/Card/CardContainer";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import Button from "../../components/Button/Button";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import { GreyTableContainer } from "../../components/Table/GreyTable";
import { getTierProgramTierLevel, toggleRebatePercent } from "../../datasource/TierProgramDatasource";
import { color } from "../../resource";

interface TierBenefit {
  tierLevelId: string;
  tierName: string;
  tierRank: number;
  minTarget: number;
  maxTarget: number | null;
  rebatePercent: number | null;
  isShowRebatePercent: boolean;
  benefitCount: number;
  reviewTriggerMode: "PERCENT" | string | null;
  reviewTriggerValue: number | null;
  isNotAutoDemotable: boolean;
}

interface TierProgramDetail {
  tierProgramId: string;
  year: number | string;
  status: "PUBLISHED" | "DRAFT" | "CLOSED" | string;
  updatedAt: string;
  updatedBy: string;
  levels: TierBenefit[];
}

const formatNumber = (value: number): string => value.toLocaleString("en-US");

const formatThreshold = (min: number, max: number | null): string => {
  if (max === null || max === undefined) return `${formatNumber(min)} ขึ้นไป`;
  return `${formatNumber(min)} - ${formatNumber(max)}`;
};

function ShopTierBenefitPage(): JSX.Element {
  const navigate = useNavigate();
  const { year: programId } = useParams<{ year: string }>();
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInAppMap, setShowInAppMap] = useState<Record<string, boolean>>({});

  const company = JSON.parse(localStorage.getItem("profile") || "null")?.company;
  const rebateLabel = company === "ICPL" ? "Rebate" : "Top Up";
  const rebateUnit = company === "ICPL" ? "(M฿)" : "(ตัน)";
  const divideMillion = (value: number | null): number | null => {
    if (value === null || value === undefined) return null;
    if (company === "ICPL") {
      return Number((value / 1000000).toFixed(2));
    }
    return value;
  }
  const userProfile = JSON.parse(localStorage.getItem("profile")!);

  const { data: tierProgramDetail, isLoading } = useQuery(
    ["tier-program-detail", programId],
    () => getTierProgramTierLevel(programId as string),
    { enabled: !!programId, refetchOnWindowFocus: false },
  );

  const detail: TierProgramDetail | undefined = tierProgramDetail?.responseData;
  const tiers: TierBenefit[] = detail?.levels ?? [];

  useEffect(() => {
    if (!tiers.length) return;
    setShowInAppMap(
      tiers.reduce<Record<string, boolean>>((acc, tier) => {
        acc[tier.tierLevelId] = tier.isShowRebatePercent;
        return acc;
      }, {}),
    );
  }, [tiers]);

  const handleToggleShowInApp = (tierLevelId: string, checked: boolean): void => {
    toggleRebatePercent(tierLevelId, checked, `${userProfile?.firstname} ${userProfile?.lastname}`).then((res)=> {
      setShowInAppMap((prev) => ({ ...prev, [tierLevelId]: checked }));
    });
  };

  const tierColumns: TableColumnsType<TierBenefit> = [
    {
      title: "ระดับ",
      dataIndex: "tierName",
      key: "tierName",
      render: (tierName: string) => (
        <Text fontFamily="Sarabun" fontSize={16} fontWeight={700} color="Text2">{tierName}</Text>
      ),
    },
    {
      title:  `เกณฑ์ยอดสะสม ${rebateUnit}`,
      dataIndex: "minTarget",
      key: "threshold",
      render: (_v: number, record: TierBenefit) => (
        <Text fontFamily="Sarabun" fontSize={16} color="Text2">
          {formatThreshold(divideMillion(record.minTarget)!,  divideMillion(record.maxTarget))}
        </Text>
      ),
    },
    {
      title: `${rebateLabel} %`,
      dataIndex: "rebatePercent",
      key: "rebatePercent",
      render: (v: number | null, record: TierBenefit) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Text fontFamily="Sarabun" fontSize={16} color="Text2">{v ?? "-"}</Text>
          {v !== null && !showInAppMap[record.tierLevelId] && (
            <Tag style={{ borderRadius: "16px" }} color={color.background1}>
              <Text fontSize={12} color="Disable" fontWeight={700}>ซ่อนจากร้านค้า</Text>
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: `แสดง % ${rebateLabel} ในแอปร้านค้า`,
      dataIndex: "isShowRebatePercent",
      key: "isShowRebatePercent",
      align: "center",
      render: (_v: boolean, record: TierBenefit) => (
        <Switch
          checked={!!showInAppMap[record.tierLevelId]}
          onChange={(checked) => handleToggleShowInApp(record.tierLevelId, checked)}
        />
      ),
    },
    {
      title: "สิทธิประโยชน์",
      dataIndex: "benefitCount",
      key: "benefitCount",
      render: (count: number) =>
        count > 0 ? (
          <Text fontFamily="Sarabun" fontSize={16} fontWeight={500} color="Text2">{count} รายการ</Text>
        ) : (
          <Text fontFamily="Sarabun" fontSize={16} color="Text3">ไม่มีสิทธิประโยชน์</Text>
        ),
    },
    {
      title: "เงื่อนไขการพิจารณา",
      dataIndex: "reviewTriggerMode",
      key: "reviewTrigger",
      render: (mode: string | null, record: TierBenefit) => {
        if (!mode) {
          return <Text fontSize={16} color="Text2">ระดับตั้งต้น</Text>;
        }
        if (mode !== "PERCENT") {
          return <Text fontSize={16} color="Text2">พิจารณาทุกร้าน</Text>;
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            <Text fontSize={16} color="Text2">
              {`เข้าคิวรอพิจารณาปรับระดับสิ้นปีที่`} {record.reviewTriggerValue ?? "-"}{"%"}
            </Text>
            {
              record.isNotAutoDemotable && (
                <Text fontFamily="Sarabun" fontSize={12} color="Text3">
                  {`ล็อกไม่ให้ระบบลดเอง`}
                </Text>
              )
            }
          </div>
        );
      },
    },
  ];

  const yearLabel = detail?.year ? `ปี ${detail.year}` : "";

  return (
    <CardContainer>
      <PageTitleNested
        title={`สิทธิประโยชน์${yearLabel}`}
        showBack
        onBack={() => navigate(-1)}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ตั้งค่าระดับ", path: -1 },
              { text: `สิทธิประโยชน์${yearLabel}`, path: window.location.pathname },
            ]}
          />
        }
        description={
          <Text fontFamily="Sarabun" fontSize={14} color="Text3">
            {detail?.status === "PUBLISHED" ? "ร้านเห็นล่วงหน้าแล้ว" : "จบรอบแล้ว"} · แก้ไขล่าสุด {detail?.updatedAt ? dayjs(detail.updatedAt).format("DD/MM/YYYY HH:mm") : "-"} โดย {detail?.updatedBy || "-"}
          </Text>
        }
        extra={
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              icon={<EditOutlined />}
              typeButton="primary"
              title="แก้ไขสิทธิประโยชน์"
              style={{ width: "auto" }}
              onClick={() => navigate(`/ShopTier/edit/benefit/${programId}`)}
            />
          </div>
        }
      />

      <div style={{ marginTop: 20 }}>
        <GreyTableContainer>
          <Table
            rowKey="tierLevelId"
            dataSource={tiers}
            columns={tierColumns}
            loading={isLoading}
            pagination={false}
          />
        </GreyTableContainer>
      </div>

      <ConfirmModal
        visible={showDuplicateConfirm}
        title="ยืนยันการทำสำเนา"
        desc={`สร้างสำเนาสิทธิประโยชน์${yearLabel} เป็นปีใหม่ ระบบจะคัดลอกเกณฑ์และสิทธิประโยชน์ทั้งหมดของปีนี้ไปยังปีที่สร้างใหม่`}
        onConfirm={() => {
          message.info("ฟังก์ชันนี้อยู่ระหว่างการพัฒนา");
          setShowDuplicateConfirm(false);
        }}
        onCancel={() => setShowDuplicateConfirm(false)}
      />

      <ConfirmModal
        visible={showDeleteConfirm}
        title="ยืนยันการลบสิทธิประโยชน์"
        desc={`ลบสิทธิประโยชน์${yearLabel} ออกจากระบบถาวร ร้านค้าจะไม่เห็นสิทธิประโยชน์นี้อีก และไม่สามารถกู้คืนได้`}
        confirmText="ลบ"
        typeButtonSubmit="danger"
        onConfirm={() => {
          message.info("ฟังก์ชันนี้อยู่ระหว่างการพัฒนา");
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </CardContainer>
  );
}

export default ShopTierBenefitPage;
